import React, { useState, useEffect } from 'react';
import { Typography, Tooltip, Box, Drawer } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { createCompanyAction, updateCompanyAction } from '../../redux/actions/company';
import CustomTextField from '../../form/CustomTextField';
import CustomSelectField from '../../form/CustomSelectField';
import strings from '../../i18n/language';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

const BRAND = '#3b95e8';
const BORDER = 'rgba(255,255,255,0.08)';
const MUTED = 'rgba(232,240,254,0.35)';

const listFeedback = [
  { value: true, label: strings.LABEL_YES },
  { value: false, label: strings.LABEL_NO },
];
const listPublic = [
  { value: true, label: strings.LABEL_COMPANIES_PUBLIC },
  { value: false, label: strings.LABEL_COMPANIES_PRIVATE },
];
const listCountries = [
  { value: 1, label: 'France' }, { value: 2, label: 'Venezuela' },
  { value: 3, label: 'Chile' }, { value: 4, label: 'Mexico' },
  { value: 5, label: 'Colombia' }, { value: 6, label: 'Argentina' },
  { value: 7, label: 'Peru' }, { value: 8, label: 'Bolivia' },
  { value: 9, label: 'Paraguay' }, { value: 10, label: 'Uruguay' },
];
const listStrategies = [
  { value: 'standard', label: 'Standard' },
  { value: 'plus', label: 'Fidelize+' },
];

const FieldRow = ({ children, tooltip }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
    <Box sx={{ flex: 1 }}>{children}</Box>
    {tooltip && (
      <Tooltip title={tooltip} placement="left">
        <InfoOutlinedIcon sx={{ fontSize: 15, color: MUTED, flexShrink: 0, cursor: 'help' }} />
      </Tooltip>
    )}
  </Box>
);

const CompanyEdit = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const company = useSelector((state) => state.company.company);

  const [name, setName] = useState(null);
  const [code, setCode] = useState(null);
  const [facebookUrl, setFacebookUrl] = useState(null);
  const [isPublic, setIsPublic] = useState(true);
  const [country, setCountry] = useState(3);
  const [strategy, setStrategy] = useState('standard');
  const [activateFeedback, setActivateFeedback] = useState(false);
  const [instagramUrl, setInstagramUrl] = useState(null);
  const [email, setEmail] = useState(null);
  const [errorName, setErrorName] = useState(false);
  const [errorEmail, setErrorEmail] = useState(false);
  const [errorUrlFacebook, setErrorUrlFacebook] = useState(false);
  const [errorUrlInstagram, setErrorUrlInstagram] = useState(false);
  const [errorCode, setErrorCode] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (company) {
      setName(company.name); setFacebookUrl(company.facebook);
      setCountry(company.CountryId); setActivateFeedback(company.giftFeedback);
      setInstagramUrl(company.instagram); setCode(company.code);
      setStrategy(company.strategy); setIsPublic(company.public);
      setEmail(company.email || null);
    } else {
      setName(null); setCode(null); setFacebookUrl(null); setInstagramUrl(null); setEmail(null);
      setIsPublic(true); setCountry(3); setStrategy('standard'); setActivateFeedback(false);
    }
    setDirty(false);
  }, [open, company]);

  useEffect(() => {
    if (company) {
      setDirty(name !== company.name || facebookUrl !== company.facebook || country !== company.CountryId || strategy !== company.strategy || isPublic !== company.public || activateFeedback !== company.giftFeedback || instagramUrl !== company.instagram || code !== company.code || (email || null) !== (company.email || null));
    } else {
      setDirty(!!(name || facebookUrl || code || email));
    }
  }, [name, facebookUrl, country, activateFeedback, instagramUrl, code, email, company, strategy, isPublic]);

  useEffect(() => {
    const re = /^[a-z0-9\sáéíóúñüäöüß.,;:!?'"\-()%]+$/i;
    setErrorName(!(name && name.length > 4 && name.length < 60 && re.test(name)));
  }, [name]);

  useEffect(() => {
    setErrorUrlFacebook(facebookUrl ? !/^https:\/\/(www\.)?facebook\.com\/[a-zA-Z0-9_.-\/]+$/i.test(facebookUrl) : false);
  }, [facebookUrl]);

  useEffect(() => {
    setErrorUrlInstagram(instagramUrl ? !/^https:\/\/(www\.)?instagram\.com\/[a-zA-Z0-9_.-]+$/i.test(instagramUrl) : false);
  }, [instagramUrl]);

  useEffect(() => {
    setErrorCode(!/^[A-Z]{3}\d{4}$/.test(code));
  }, [code]);

  // Optional: empty means no alerts or reports for this shop
  useEffect(() => {
    setErrorEmail(email ? !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim()) : false);
  }, [email]);

  const save = () => {
    if (errorName) { toast.error(strings.LABEL_COMPANIES_NAME_ERROR); return; }
    if (errorCode) { toast.error(strings.LABEL_COMPANIES_CODE_ERROR); return; }
    if (errorUrlFacebook) { toast.error(strings.LABEL_COMPANIES_URL_FACEBOOK_ERROR); return; }
    if (errorUrlInstagram) { toast.error(strings.LABEL_COMPANIES_URL_INSTAGRAM_ERROR); return; }
    if (errorEmail) { toast.error(strings.LABEL_COMPANIES_EMAIL_ERROR); return; }
    const formData = new FormData();
    formData.append('name', name);
    formData.append('code', code);
    formData.append('public', isPublic);
    formData.append('CountryId', country);
    formData.append('strategy', strategy);
    if (facebookUrl) formData.append('facebook', facebookUrl);
    if (instagramUrl) formData.append('instagram', instagramUrl);
    formData.append('giftFeedback', activateFeedback);
    // Always sent, empty included, so it can be cleared
    formData.append('email', email ? email.trim() : '');
    if (company?.id) dispatch(updateCompanyAction(formData, company.id));
    else dispatch(createCompanyAction(formData));
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 480,
          background: '#0d1b36',
          borderLeft: `1px solid ${BORDER}`,
          boxShadow: '-24px 0 60px rgba(0,0,0,0.4)',
        },
      }}
    >
      <ToastContainer position="bottom-right" />

      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 3, py: 2, borderBottom: `1px solid ${BORDER}`, flexShrink: 0 }}>
        <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#e8f0fe' }}>
          {company ? company.name : 'Nueva empresa'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <button onClick={onClose} style={{
            padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 500,
            background: 'rgba(255,255,255,0.05)', color: MUTED,
            border: `1px solid ${BORDER}`, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 5,
          }}>
            <CloseRoundedIcon style={{ fontSize: 15 }} /> Cancelar
          </button>
          <button onClick={save} disabled={!dirty} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
            background: dirty ? BRAND : 'rgba(59,149,232,0.15)',
            color: dirty ? '#fff' : 'rgba(59,149,232,0.4)',
            border: 'none', cursor: dirty ? 'pointer' : 'default',
            boxShadow: dirty ? '0 4px 14px rgba(59,149,232,0.3)' : 'none',
            transition: 'all 0.2s',
          }}>
            <SaveRoundedIcon style={{ fontSize: 15 }} /> Guardar
          </button>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 0.5, overflowY: 'auto' }}>
        <FieldRow tooltip={strings.LABEL_COMPANIES_TOOLTIP_NAME}>
          <CustomTextField margin="dense" id="name" label={strings.LABEL_INPUT_NAME} name="name" value={name} onChange={setName} autoFocus />
        </FieldRow>
        <FieldRow tooltip={strings.LABEL_COMPANIES_TOOLTIP_EMAIL}>
          <CustomTextField margin="dense" id="company-email" label={strings.LABEL_INPUT_COMPANY_EMAIL} name="company-email" type="email" value={email} onChange={setEmail} />
        </FieldRow>
        <FieldRow>
          <CustomSelectField margin="dense" onChange={setCountry} title={strings.LABEL_COUNTRY} id="country" value={country} defaultValues={listCountries} />
        </FieldRow>
        <FieldRow>
          <CustomSelectField margin="dense" onChange={setStrategy} title="Estrategia" id="strategy" value={strategy} defaultValues={listStrategies} />
        </FieldRow>
        <FieldRow tooltip={strings.LABEL_COMPANIES_TOOLTIP_TYPE}>
          <CustomSelectField margin="dense" onChange={setIsPublic} title={strings.LABEL_NOTIFICATION_TYPE} id="public" value={isPublic} defaultValues={listPublic} />
        </FieldRow>
        <FieldRow tooltip={strings.LABEL_COMPANIES_FEEDBACK_TYPE}>
          <CustomSelectField margin="dense" onChange={setActivateFeedback} title={strings.LABEL_COMPANIES_FEEDBACK} id="feedback" value={activateFeedback} defaultValues={listFeedback} />
        </FieldRow>
        <FieldRow tooltip={strings.LABEL_COMPANIES_TOOLTIP_FACEBOOK}>
          <CustomTextField margin="dense" id="facebook-url" label={strings.LABEL_INPUT_FACEBOOK_URL} name="facebook-url" value={facebookUrl} onChange={setFacebookUrl} />
        </FieldRow>
        <FieldRow tooltip={strings.LABEL_COMPANIES_TOOLTIP_INSTAGRAM}>
          <CustomTextField margin="dense" id="instagram-url" label={strings.LABEL_INPUT_INSTAGRAM_URL} name="instagram-url" value={instagramUrl} onChange={setInstagramUrl} />
        </FieldRow>
        <FieldRow tooltip={strings.LABEL_COMPANIES_TOOLTIP_CODE}>
          <CustomTextField margin="dense" id="code" label={strings.LABEL_INPUT_CODE} name="code" value={code} onChange={(v) => setCode(v.toUpperCase())} />
        </FieldRow>
      </Box>
    </Drawer>
  );
};

export default CompanyEdit;
